import React, { useState } from 'react';
import type { BreadcrumbItem, BreadcrumbProps } from './types';

// ─── Default separator ────────────────────────────────────────────────────────

function DefaultSeparator() {
  return (
    <svg
      className="h-3.5 w-3.5 text-neutral-400 dark:text-neutral-500 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Builds the visible item list applying maxItems collapsing.
 * When collapsed, keeps first item + "..." ellipsis + last two items.
 * On mobile (handled via CSS) only parent + current are shown.
 */
function buildVisible(
  items: BreadcrumbItem[],
  maxItems: number | undefined,
  expanded: boolean,
): Array<BreadcrumbItem | null> {
  // null = ellipsis slot
  if (!maxItems || items.length <= maxItems || expanded) return items;
  // Always show: first item, ellipsis, last two items
  const head = items.slice(0, 1);
  const tail = items.slice(-2);
  return [...head, null, ...tail];
}

// ─── Breadcrumb ───────────────────────────────────────────────────────────────

export function Breadcrumb({
  items,
  separator,
  maxItems,
}: BreadcrumbProps) {
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) return null;

  const sep = separator !== undefined ? separator : <DefaultSeparator />;
  const visible = buildVisible(items, maxItems, expanded);

  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex flex-wrap items-center gap-1">
        {visible.map((item, index) => {
          const isLast = index === visible.length - 1;

          // ── Ellipsis slot ─────────────────────────────────────────────────
          if (item === null) {
            return (
              <React.Fragment key="ellipsis">
                <li className="flex items-center">
                  <button
                    type="button"
                    className="px-1 text-sm text-neutral-500 dark:text-neutral-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
                    onClick={() => setExpanded(true)}
                    aria-label="Show all breadcrumb items"
                  >
                    &hellip;
                  </button>
                </li>
                {!isLast && (
                  <li aria-hidden="true" className="flex items-center">
                    {sep}
                  </li>
                )}
              </React.Fragment>
            );
          }

          const isCurrentPage = index === visible.length - 1;

          // ── Mobile visibility: hide intermediate items (show only last two) ─
          // First item (index 0) and last item are always visible.
          // Middle items (not first, not last) are hidden on mobile.
          const mobileVisibility =
            index === 0 || isCurrentPage ? '' : 'hidden sm:flex';

          return (
            <React.Fragment key={`${item.label}-${index}`}>
              <li className={`flex items-center ${mobileVisibility}`}>
                {isCurrentPage ? (
                  <span
                    className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate max-w-[200px]"
                    aria-current="page"
                  >
                    {item.label}
                  </span>
                ) : item.href ? (
                  <a
                    href={item.href}
                    className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors truncate max-w-[160px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
                  >
                    {item.label}
                  </a>
                ) : (
                  <span className="text-sm text-neutral-500 dark:text-neutral-400 truncate max-w-[160px]">
                    {item.label}
                  </span>
                )}
              </li>

              {/* Separator — hidden on mobile for intermediate items */}
              {!isLast && (
                <li
                  aria-hidden="true"
                  className={`flex items-center ${mobileVisibility}`}
                >
                  {sep}
                </li>
              )}
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
